import { Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { KbdChip } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";

/**
 * Бутон от лентата под масите (Нова · Добави · Закрий · Опции и изнесените
 * от „Опции"): ghost / primary / danger вариант, клавишен чип на десктоп,
 * spinner при `loading`, и `lockedTooltip` — подсказката на заключено действие
 * (локален режим, docs/edge.md). Изваден от TableFooter.
 */
type FooterVariant = "ghost" | "primary" | "danger";

/**
 * Колко е свит бутонът, когато лентата е пълна (`footer/useFooterDensity.ts`):
 * normal → compact (по-малък отстъп, без минимална ширина) → tight (още
 * по-малък отстъп, по-дребен текст, без клавишния чип, който иначе застъпва
 * текста) → squeeze (бутонът се свива заедно с другите, текстът се реже с „…").
 * Важи само извън телефон — там лентата е фиксирана решетка.
 */
export type FooterDensity = "normal" | "compact" | "tight" | "squeeze";

const DENSITY_CLASS: Record<FooterDensity, string> = {
  normal: "px-7 min-w-[132px] text-[15px] gap-2 shrink-0",
  compact: "px-4 min-w-0 text-[15px] gap-2 shrink-0",
  tight: "px-3 min-w-0 text-[14px] gap-1.5 shrink-0",
  squeeze: "px-2 min-w-[40px] text-[13px] gap-1 shrink overflow-hidden",
};

export interface FooterButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: FooterVariant;
  shortcut?: string;
  loading?: boolean;
  /** Подсветка на тригера „Опции" при отворен popover. */
  active?: boolean;
  /** Подсказка при заключено действие (локален режим, docs/edge.md). */
  lockedTooltip?: string;
  density?: FooterDensity;
  /** Само за невидимия ред за мерене (`footer/FooterMeasureRow`): етикетът е
   *  в `::after`, не в DOM-а — иначе търсенето по текст (тестове, достъпност)
   *  би намирало всеки бутон по няколко пъти. Ширината е същата. */
  measureOnly?: boolean;
}

export const FooterButton = ({
  icon,
  label,
  onClick,
  disabled,
  variant = "ghost",
  shortcut,
  loading,
  active,
  lockedTooltip,
  density = "normal",
  measureOnly = false,
}: FooterButtonProps) => {
  const { isNarrow, isMobile } = useViewport();

  // Телефон (<480): показваме само текста на не-primary бутоните, скриваме иконата.
  const hideIcon = isMobile && variant !== "primary";
  const showShortcut =
    !!shortcut && !isNarrow && (density === "normal" || density === "compact");
  const squeezed = !isNarrow && density === "squeeze";

  const variantClass =
    variant === "primary"
      ? "bg-pos-accent hover:bg-pos-accent-ink text-white font-bold"
      : variant === "danger"
        ? "border border-pos-danger-brd bg-pos-danger-bg text-pos-danger-ink font-semibold"
        : `border border-pos-ctl text-pos-ink2 font-semibold ${
            active ? "bg-fill-2" : "bg-pos-card"
          }`;

  const kbdColor =
    variant === "primary"
      ? "text-white/60"
      : variant === "danger"
        ? "text-pos-faint"
        : "text-pos-faint";

  const button = (
    <button
      type="button"
      onClick={onClick}
      // Заключеното действие е И disabled, не само `pointer-events-none`:
      // иначе бутонът изглежда напълно работещ и просто мълчи при натискане
      // (същото правило като в OrderFooter, OptionsPopover, TransferWaiterModal).
      disabled={disabled || loading || !!lockedTooltip}
      title={squeezed ? label : undefined}
      className={`relative inline-flex items-center justify-center whitespace-nowrap cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed ${
        lockedTooltip ? "pointer-events-none" : ""
      } ${
        isNarrow
          ? "h-[46px] rounded-[11px] px-2 min-w-0 text-[12.5px] gap-2 shrink-0"
          : `h-[52px] rounded-[13px] ${DENSITY_CLASS[density]}`
      } ${variantClass}`}
    >
      {!hideIcon && (
        <span
          className={`inline-flex shrink-0 items-center justify-center ${
            isNarrow ? "text-[15px]" : "text-base"
          }`}
        >
          {loading ? <LoadingOutlined /> : icon}
        </span>
      )}
      {measureOnly ? (
        <span data-label={label} className="truncate after:content-[attr(data-label)]" />
      ) : (
        <span className="truncate">{label}</span>
      )}
      {showShortcut && !measureOnly && (
        <KbdChip shortcut={shortcut} colorClass={kbdColor} />
      )}
    </button>
  );
  // Обвивката носи подсказката на заключено действие: disabled бутон не
  // получава mouse събития, затова той е pointer-events-none.
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span
        className={`flex min-w-0 cursor-not-allowed [&>button]:flex-1 ${
          squeezed ? "shrink" : "shrink-0"
        }`}
      >
        {button}
      </span>
    </Tooltip>
  ) : (
    button
  );
};
